import {
    formatDateTimeAndTimezone,
    formatTimeAndTimezone,
} from "@impero-com/common/utils/formatting";
import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import moment from "moment-timezone";

import {
    PendingAction,
    PendingActionPaginatedListDto,
    IanaTimezoneName,
    TActTableRowListDto,
    TestingActivityCurrentStep,
    UserTagCategoryOverview,
    UserTagOverview,
} from "api/generated/model";
import { LegacyRoute } from "legacyRoutes";
import { getCurrentStepDueDate } from "org/controlTesting/overdueUtils";
import { dropUndefinedTags } from "utils/tags/tagRecord";

import { PENDING_ACTIVITIES_REDIRECT_SUFIX } from "./consts";
import {
    PendingActionType,
    PendingActivityTableRow,
    PendingControlActivityTableRow,
    PendingTestActivityTableRow,
} from "./types";

export function buildPendingActivities(
    data: PendingActionPaginatedListDto
): PendingControlActivityTableRow[] {
    const pendingActivities: PendingControlActivityTableRow[] =
        data.content.map((item): PendingControlActivityTableRow => {
            const resource: PendingAction = item.resource;
            return {
                id: getPendingActivityId(resource),
                action: resource.type,
                activityName: resource.data.title,
                tags: dropUndefinedTags(resource.data.tags),
                controlProgramName: resource.data.programName ?? "",
                userPoolName: resource.data.userPoolName ?? null,
                claimantName: getClaimantName(resource),
                pendingStepDueDate: resource.data.dueDate,
                entity: resource.data.entity ?? null,
                controlResultCreated: resource.data.controlResultCreated,
            };
        });
    return pendingActivities;
}

export function buildPendingTestRows(
    data: TActTableRowListDto
): PendingTestActivityTableRow[] {
    const { tags, tagCategories, testingProgramNamesDto } =
        data.linkedResources;
    const toTagNameRecord = createTagNameRecordResolver(tags, tagCategories);
    return data.content.flatMap(
        ({ resource }): PendingTestActivityTableRow[] => {
            const action = getPendingTestAction(resource.status);
            if (action === null) {
                return [];
            }
            return [
                {
                    id: resource.activityId,
                    action,
                    activityName: resource.title,
                    pendingStepDueDate:
                        getCurrentStepDueDate(resource) ?? resource.dueDate,
                    tags: toTagNameRecord(resource.tags),
                    testProgramName:
                        testingProgramNamesDto[resource.testingProgramId]
                            ?.resource ?? "",
                    entitiesToTest: toTagNameRecord(resource.samplingTags),
                },
            ];
        }
    );
}

function getPendingTestAction(
    status: TestingActivityCurrentStep
): PendingTestActivityTableRow["action"] | null {
    switch (status) {
        case "responsible":
            return "Test";
        case "firstReviewer":
        case "secondReviewer":
            return "TestReview";
        case "completed":
            return null;
        default:
            return assertNever(status);
    }
}

/**
 * Returns a function that turns tag ids into the `Record<categoryName, tagName[]>` shape.
 * Ids missing from `tags`, or whose category is missing, are skipped.
 */
function createTagNameRecordResolver(
    tags: UserTagOverview[],
    tagCategories: UserTagCategoryOverview[]
): (tagIds: string[]) => Record<string, string[]> {
    const tagsById = new Map(tags.map((tag) => [tag.id, tag]));
    const categoryNamesById = new Map(
        tagCategories.map((category) => [category.id, category.name])
    );
    return (tagIds) => {
        const tagNamesByCategory = new Map<string, string[]>();
        for (const tagId of tagIds) {
            const tag = tagsById.get(tagId);
            const categoryName =
                tag && categoryNamesById.get(tag.tagCategoryId);
            if (tag && categoryName) {
                const tagNames = tagNamesByCategory.get(categoryName);
                if (tagNames) {
                    tagNames.push(tag.name);
                } else {
                    tagNamesByCategory.set(categoryName, [tag.name]);
                }
            }
        }
        return Object.fromEntries(tagNamesByCategory);
    };
}

export function isOverduePendingActivities(
    pendingActivities: PendingActivityTableRow[]
): boolean[] {
    const now = moment();
    return pendingActivities.map((row) =>
        moment(row.pendingStepDueDate).isBefore(now)
    );
}

function getPendingActivityId(resource: PendingAction): string {
    switch (resource.type) {
        case "Control":
            return resource.data.resultId;
        case "Review":
            return resource.data.reviewId;
        default:
            return assertNever(resource);
    }
}

function getClaimantName(resource: PendingAction): string | null {
    switch (resource.type) {
        case "Control": {
            const controlAction = resource.data;
            const firstName = controlAction.claimantFirstName;
            const lastName = controlAction.claimantLastName;
            return firstName && lastName ? `${firstName} ${lastName}` : null;
        }
        case "Review":
            return null; // Review action does not have claimant
        default:
            return assertNever(resource);
    }
}

export function isControlActivityRow(
    row: PendingActivityTableRow
): row is PendingControlActivityTableRow {
    return row.action === "Control" || row.action === "Review";
}

const PENDING_ACTION_TYPES: Record<PendingActionType, true> = {
    Control: true,
    Review: true,
    Test: true,
    TestReview: true,
};

export function isPendingActionType(
    value: unknown
): value is PendingActionType {
    return (
        typeof value === "string" && Object.hasOwn(PENDING_ACTION_TYPES, value)
    );
}

export function computeAction(t: TFunction, action: PendingActionType): string {
    switch (action) {
        case "Control":
            return t("Dashboard_PendingActions_Action_Perform");
        case "Review":
            return t("Dashboard_PendingActions_Action_Review");
        case "Test":
            return t("Dashboard_PendingActions_Action_PerformTest");
        case "TestReview":
            return t("Dashboard_PendingActions_Action_PerformTestReview");
        default:
            return assertNever(action);
    }
}

export function buildRowLinkUrl(
    id: string,
    action: PendingControlActivityTableRow["action"]
): string {
    switch (action) {
        case "Control":
            return `${LegacyRoute.CONTROL_RESULT_COLLECT}/${id}${PENDING_ACTIVITIES_REDIRECT_SUFIX}`; //TODO: sc-35428 Figure out how to handle this in legacy once we deprecate the legacy endpoint: "/Dashboard/PendingActions"
        case "Review":
            return `${LegacyRoute.CONTROL_RESULT_REVIEW}/${id}${PENDING_ACTIVITIES_REDIRECT_SUFIX}`; //TODO: sc-35428 Figure out how to handle this in legacy once we deprecate the legacy endpoint: "/Dashboard/PendingActions"
        default:
            return assertNever(action);
    }
}

export function buildDueDateWithPrefix(
    t: TFunction,
    dueDate: string,
    timezoneName: IanaTimezoneName
): string {
    // Compare only calendar dates, not times
    // Using .startOf("day") prevents shifting to "yesterday" or "tomorrow" if the current time is past the due time, but still the same day
    const dayOffset = dueDate
        ? moment
              .tz(dueDate, timezoneName)
              .startOf("day")
              .diff(moment.tz(timezoneName).startOf("day"), "days")
        : null;
    const prefix = buildPrefixForDueDate(t, dayOffset);
    if (prefix) {
        // Only show time and timezone if prefix is present
        const formattedTime = formatTimeAndTimezone(dueDate, {
            name: timezoneName,
        });
        return `${prefix}${formattedTime}`;
    } else {
        return formatDateTimeAndTimezone(dueDate, { name: timezoneName });
    }
}

export function buildPrefixForDueDate(
    t: TFunction,
    dayOffset: number | null
): string | null {
    switch (dayOffset) {
        case 1:
            return t("Dashboard_PendingActions_DueTomorrowAt") + " ";
        case 0:
            return t("Dashboard_PendingActions_DueTodayAt") + " ";
        case -1:
            return t("Dashboard_PendingActions_DueYesterdayAt") + " ";
        default:
            return null;
    }
}
